import { useCallback, useState } from 'react';
import { useAxiosInstance } from '@gov-nx/api';
import type { DownloadEPetitionSubscription } from '@gov-nx/core/events';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { usePetitionDownload } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { DownloadCallback } from '../Download/types';

export const DownloadEPetitionListener = () => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Service);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { download } = useDownloadEvents();
	const { axiosInstance } = useAxiosInstance();
	const [callbacks, setCallbacks] = useState<{
		[key: number]: Nullable<DownloadCallback<number>['callback']>;
	}>({});

	const { mutate } = usePetitionDownload({
		onSuccess: (data) => {
			download({
				blob: data.blob,
				id: data.petitionId.toString(),
				antivirus: data.antivirus,
				fileName: data.fileName,
				callback: (id, success, error) => {
					if (success) {
						successMessage(data.fileName);
						onDownload(data.petitionId);
					}
					if (error) {
						errorMessage();
						onDownload(data.petitionId, error);
					}
				},
			});
		},
		onError: () => {
			errorMessage();
		},
	});

	const onEPetitionDownload = useCallback(
		(
			_: DownloadEPetitionSubscription['type'],
			{ petitionId, callback }: DownloadEPetitionSubscription['payload']
		) => {
			if (callback) {
				setCallbacks((state) => ({
					...state,
					[petitionId]: callback,
				}));
			}

			setTimeout(() => {
				mutate({ petitionId, axiosInstance });
			}, 0);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[mutate]
	);

	useDownloadEvents({
		onEPetitionDownload,
	});

	const onDownload = useCallback(
		(petitionId: number, error?: Error) => {
			const callbackFunction = callbacks[petitionId];
			if (callbackFunction) {
				callbackFunction(petitionId, !error, error ?? null);
				setCallbacks((state) => ({ ...state, [petitionId]: null }));
			}
		},
		[callbacks]
	);

	const successMessage = (fileName: string) => {
		toastMessageSuccess(
			t('dokument.stazeni-dokumentu-uspech', {
				fileName,
			})
		);
	};

	const errorMessage = () => {
		toastMessageError(t('dokument.stazeni-dokumentu-chyba'));
	};

	return null;
};
